import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Activity, 
  Database, 
  Users, 
  FileText, 
  AlertTriangle, 
  CheckCircle,
  RefreshCw,
  Server,
  Shield
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { SSLVerification } from "./SSLVerification";
import { ErrorMonitoring } from "./ErrorMonitoring";

interface SystemMetrics {
  users: { total: number; active: number };
  listings: { total: number; active: number };
  referrals: { total: number; pending: number };
  alerts: { total: number; critical: number; unresolved: number };
  timestamp: string;
}

interface SystemHealth {
  database: boolean;
  auth: boolean;
  storage: boolean;
  timestamp: string;
  allSystemsOperational: boolean;
}

interface BackupStatus {
  database: { status: string; lastBackup: string };
  files: { status: string; lastSync: string };
  verification: { status: string; lastCheck: string };
}

export function ProductionDashboard() {
  const [metrics, setMetrics] = useState<SystemMetrics | null>(null);
  const [systemHealth, setSystemHealth] = useState<SystemHealth | null>(null);
  const [backupStatus, setBackupStatus] = useState<BackupStatus | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const loadSystemMetrics = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('system-monitoring', {
        body: { action: 'get_metrics' }
      });

      if (error) {
        console.error('Error loading metrics:', error);
        toast.error("Failed to load system metrics");
        return;
      }

      setMetrics(data.metrics);
    } catch (error) {
      console.error('Error loading metrics:', error);
      toast.error("Failed to load system metrics");
    }
  };

  const checkSystemHealth = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('system-monitoring', {
        body: { action: 'check_system' }
      });

      if (error) {
        console.error('Error checking system health:', error);
        toast.error("Failed to check system health");
        return;
      }

      setSystemHealth({
        database: data.checks.database,
        auth: data.checks.auth,
        storage: data.checks.storage,
        timestamp: data.checks.timestamp,
        allSystemsOperational: data.allSystemsOperational
      });
    } catch (error) {
      console.error('Error checking system health:', error);
      toast.error("Failed to check system health");
    }
  };

  const runBackupTest = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('backup-test', {
        body: { testType: 'basic' }
      });

      if (error) {
        console.error('Error running backup test:', error);
        toast.error("Failed to run backup verification");
        return;
      }

      // Transform backup test results into status format
      setBackupStatus({
        database: {
          status: data.results.database?.status || 'unknown',
          lastBackup: new Date().toISOString()
        },
        files: {
          status: data.results.storage?.status || 'unknown',
          lastSync: new Date().toISOString()
        },
        verification: {
          status: data.overallStatus,
          lastCheck: new Date().toISOString()
        }
      });

      if (data.overallStatus === 'success') {
        toast.success("All backup systems verified successfully");
      } else {
        toast.warning("Some backup systems need attention");
      }

    } catch (error) {
      console.error('Error running backup test:', error);
      toast.error("Failed to run backup verification");
    } finally {
      setIsLoading(false);
    }
  };

  const refreshAllData = async () => {
    setIsLoading(true);
    await Promise.all([
      loadSystemMetrics(),
      checkSystemHealth(),
      runBackupTest()
    ]);
    setIsLoading(false);
  };

  useEffect(() => {
    refreshAllData();
  }, []);

  const getStatusBadge = (status: boolean | string) => {
    if (typeof status === 'boolean') {
      return status ? (
        <Badge variant="default" className="bg-green-500">
          <CheckCircle className="w-3 h-3 mr-1" />Online
        </Badge>
      ) : (
        <Badge variant="destructive">
          <AlertTriangle className="w-3 h-3 mr-1" />Offline
        </Badge>
      );
    }
    
    switch (status) {
      case 'success':
        return <Badge variant="default" className="bg-green-500"><CheckCircle className="w-3 h-3 mr-1" />Success</Badge>;
      case 'error':
        return <Badge variant="destructive"><AlertTriangle className="w-3 h-3 mr-1" />Error</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Production Dashboard</h1>
          <p className="text-muted-foreground">Monitor system health, performance, and security</p>
        </div>
        <Button onClick={refreshAllData} disabled={isLoading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
          {isLoading ? "Refreshing..." : "Refresh All"}
        </Button>
      </div>

      {/* System Health Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">Overall Status</p>
                <p className="text-2xl font-bold">
                  {systemHealth?.allSystemsOperational ? "Operational" : "Issues"}
                </p>
              </div>
              <Activity className={`w-8 h-8 ${systemHealth?.allSystemsOperational ? 'text-green-500' : 'text-red-500'}`} />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">Total Users</p>
                <p className="text-2xl font-bold">{metrics?.users.total || 0}</p>
                <p className="text-xs text-muted-foreground">{metrics?.users.active || 0} active</p>
              </div>
              <Users className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">Listings</p>
                <p className="text-2xl font-bold">{metrics?.listings.total || 0}</p>
                <p className="text-xs text-muted-foreground">{metrics?.listings.active || 0} active</p>
              </div>
              <FileText className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">Referrals</p>
                <p className="text-2xl font-bold">{metrics?.referrals.total || 0}</p>
                <p className="text-xs text-muted-foreground">{metrics?.referrals.pending || 0} pending</p>
              </div>
              <Database className="w-8 h-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="health" className="space-y-4">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="health">System Health</TabsTrigger>
          <TabsTrigger value="security">Security</TabsTrigger>
          <TabsTrigger value="monitoring">Monitoring</TabsTrigger>
          <TabsTrigger value="backups">Backups</TabsTrigger>
        </TabsList>

        <TabsContent value="health" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Server className="w-5 h-5" />
                System Health Status
              </CardTitle>
              <CardDescription>Real-time status of core system components</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {systemHealth && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="flex items-center justify-between p-3 border rounded">
                    <span className="font-medium">Database</span>
                    {getStatusBadge(systemHealth.database)}
                  </div>
                  <div className="flex items-center justify-between p-3 border rounded">
                    <span className="font-medium">Authentication</span>
                    {getStatusBadge(systemHealth.auth)}
                  </div>
                  <div className="flex items-center justify-between p-3 border rounded">
                    <span className="font-medium">File Storage</span>
                    {getStatusBadge(systemHealth.storage)}
                  </div>
                </div>
              )}
              
              <div className="text-sm text-muted-foreground">
                Last checked: {systemHealth?.timestamp ? new Date(systemHealth.timestamp).toLocaleString() : 'Never'}
              </div>

              <Button onClick={checkSystemHealth} variant="outline">
                <RefreshCw className="w-4 h-4 mr-2" />
                Check System Health
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="security" className="space-y-4">
          <SSLVerification />
        </TabsContent>

        <TabsContent value="monitoring" className="space-y-4">
          <ErrorMonitoring />
        </TabsContent>

        <TabsContent value="backups" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="w-5 h-5" />
                Backup Verification
              </CardTitle>
              <CardDescription>Verify backup systems and disaster recovery readiness</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {backupStatus && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 border rounded">
                    <div>
                      <span className="font-medium">Database Backups</span>
                      <p className="text-sm text-muted-foreground">
                        Last backup: {new Date(backupStatus.database.lastBackup).toLocaleString()}
                      </p>
                    </div>
                    {getStatusBadge(backupStatus.database.status)}
                  </div>

                  <div className="flex items-center justify-between p-3 border rounded">
                    <div>
                      <span className="font-medium">File Storage Sync</span>
                      <p className="text-sm text-muted-foreground">
                        Last sync: {new Date(backupStatus.files.lastSync).toLocaleString()}
                      </p>
                    </div>
                    {getStatusBadge(backupStatus.files.status)}
                  </div>

                  <div className="flex items-center justify-between p-3 border rounded">
                    <div>
                      <span className="font-medium">Backup Verification</span>
                      <p className="text-sm text-muted-foreground">
                        Last check: {new Date(backupStatus.verification.lastCheck).toLocaleString()}
                      </p>
                    </div>
                    {getStatusBadge(backupStatus.verification.status)}
                  </div>
                </div>
              )}

              <Button onClick={runBackupTest} disabled={isLoading}>
                {isLoading ? "Running Tests..." : "Run Backup Verification"}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}